import { ACTION_MODAL_TYPE, type ActionModalType } from 'actions/openModalAction';
import { HpSvgIconTypes, SvgIcon } from '@avantia/style-guide';
import { HpAlert } from 'components/shared/alert';
import { Modal } from 'react-bootstrap';
import { Button } from 'react-bootstrap';
import { Button as HpButton } from 'components/shared/button';
import { useAppDispatch } from 'store/hooks';
import { closeActionModalAction, openCustomerContactUsModal } from 'actions/openModalAction';

import './actionModal.scss';

interface ActionModalProps {
  show: boolean;
  modalType: ActionModalType;
  alertMessage?: string;
}

interface ModalContent {
  title: string;
  content: JSX.Element;
  actions?: React.ReactNode;
}

export const ActionModal = ({ show, modalType, alertMessage }: ActionModalProps) => {
  const dispatch = useAppDispatch();

  const handleClose = () => {
    dispatch(closeActionModalAction());
  };

  const closeButton = <HpButton handleClick={handleClose} text="CLOSE" id="modal-close-button" variant="reversed" />;

  const renderContent = (modalType: ActionModalType): ModalContent | null => {
    switch (modalType) {
      case ACTION_MODAL_TYPE.AGENT_FEATURE_UNAVAILABLE:
        return {
          title: 'Sorry, this feature is coming soon',
          content: (
            <>
              {alertMessage && (
                <HpAlert
                  message={<p className="error-modal-text">{alertMessage}</p>}
                  alertLevel="error"
                  iconType={'exclamation' as HpSvgIconTypes}
                  className="error-modal-alert"
                />
              )}
              <p className="av-text-large error-modal-text">
                This feature is not currently available within the customer portal. You can make this change using TCAS
                or another internal tool.
              </p>
            </>
          ),
          actions: closeButton
        };

      case ACTION_MODAL_TYPE.CUSTOMER_FEATURE_UNAVAILABLE:
        return {
          title: 'Get in touch',
          content: (
            <>
              <p className="av-text-large error-modal-text">
                You can&apos;t complete this change online. Please{' '}
                <button
                  type="button"
                  id="modal-contact-us"
                  className="modal-contact-us-link"
                  onClick={() => dispatch(openCustomerContactUsModal())}>
                  contact us
                </button>{' '}
                and we&apos;ll make the change for you.
              </p>
            </>
          ),
          actions: closeButton
        };

      case ACTION_MODAL_TYPE.CUSTOMER_CONTACT_US:
        return {
          title: 'Contact us',
          content: (
            <>
              <p className="av-text-large error-modal-text bold-text">Chat with us</p>
              <p className="av-text-large error-modal-text ">
                The easiest way to speak with us is to chat with one of our insurance experts (Live chat is open from
                Monday to Friday 8am – 8pm, Saturday to Sunday from 9am – 6pm).
              </p>
              <Button id="atlas-chat" className="action-modal-primary-button">
                <p className="av-text-large policy-button-text">Live chat</p>
              </Button>
              <p className="av-text-large error-modal-text bold-text">Call us</p>
              <p className="av-text-large error-modal-text ">
                You can also speak with one of our insurance experts by calling the number below (we’re open from Monday
                to Friday 9am – 8pm and Saturday from 9am – 1pm).
              </p>
              <p className="phone-number">0330 660 1000</p>
            </>
          )
        };

      default:
        return null;
    }
  };

  const { title, content, actions } = renderContent(modalType) || {};

  return (
    <Modal show={show} onHide={handleClose} id="action-modal" centered>
      <div className="modal-header">
        <h2>{title}</h2>
        <a onClick={handleClose} data-testid="close-button" id="close-button">
          <SvgIcon type={'cross' as HpSvgIconTypes} size="small" />
        </a>
      </div>
      <div className="modal-body">{content}</div>
      {actions && <div className="modal-actions">{actions}</div>}
    </Modal>
  );
};
